if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.dataset.requireSize === 'true' || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const monogramToggle = Array.from(this.form.elements).find(
          (input) => input.classList?.contains('monogram-toggle')
        );
        const customizer = monogramToggle?.closest('monogram-customizer');
        // Fail closed if the customizer script has not finished loading.
        if (monogramToggle?.checked && !customizer?.prepareCartData) {
          this.handleErrorMessage('Monogram options are still loading. Please try again.');
          return;
        }
        const monogram = customizer?.prepareCartData();
        if (monogram === false) return;
        // Check the values that will actually be sent, before adding either item.
        if (monogram && ![
          'Monogram Order', 'Monogram Style', 'Monogram Text', 'Monogram Embroidery Color',
        ].every((name) => typeof monogram.properties?.[name] === 'string' && monogram.properties[name].trim())) {
          this.handleErrorMessage('We could not prepare your monogram details. Please refresh the page and try again.');
          return;
        }
        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (monogram) {
          const properties = {};
          for (const [name, value] of formData.entries()) {
            const match = name.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          }
          // These properties come directly from the validated customizer fields.
          Object.assign(properties, monogram.properties);
          const quantity = Number(formData.get('quantity') || 1);
          const group = crypto.randomUUID();
          properties._monogram_group = group;
          const product = { id: formData.get('id'), quantity, properties };
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) product.selling_plan = sellingPlan;
          const payload = {
            items: [product, { id: monogram.feeVariantId, quantity, properties: { _monogram_group: group, _monogram_fee: 'true' } }],
          };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const result = await response.json();
            if (!response.ok && !result.status) result.status = response.status;
            return result;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (monogram) {
              const addedProduct = response.items?.find((item) =>
                String(item.variant_id || item.id) === String(formData.get('id'))
              );
              const detailsSaved = addedProduct && Object.entries(monogram.properties).every(
                ([name, value]) => String(addedProduct.properties?.[name] ?? '') === value
              );
              if (!detailsSaved) {
                this.handleErrorMessage('We could not confirm your monogram details were saved. Please check your cart and contact us before checking out if any details are missing.');
                return;
              }
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            // Cart notifications need the original product key for multi-item responses.
            if (response.items) response.key = response.items[0]?.key;
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const quickViewModal = document.getElementById('product-quick-view');
            
            if (quickViewModal) {
              quickViewModal.classList.remove('quick-view-loaded');
              document.body.classList.remove('quickview-open');
            }
            
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('We could not confirm your cart update. Please check your cart before trying again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.dataset.requireSize === 'true') {
          this.submitButton.disabled = true;
          this.submitButtonText.textContent = 'Select Size';
          return;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButton.hasAttribute('data-preorder') ? 
          window.variantStrings.preOrder : 
          window.variantStrings.addToCart;
          
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
